import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../../../static/constants'
import { postService } from 'core/services'
import { LightgalleryProvider, LightgalleryItem, useLightgallery } from 'react-lightgallery'
import I18n from 'core/i18n'
import SvgIcon from 'components/atoms/SvgIcon'

import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { FreeMode, Navigation, Thumbs } from 'swiper'

SwiperCore.use([FreeMode, Navigation, Thumbs])

interface IProps {
  post: any
}

export const TravelerPhotos: React.FC<IProps> = props => {
  const [reviewImages, setReviewImages] = React.useState([])

  const syncReviewImages = React.useCallback(async () => {
    const { reviewImages: data } = await postService.getReviewImages(props.post.id)
    setReviewImages(data)
  }, [setReviewImages, postService])

  function ShowMoreReviewImages() {
    const { openGallery } = useLightgallery()

    const open = React.useCallback(() => {
      openGallery('travelerPhotos')
    }, [])
    return (
      <div className="ShowAll">
        <button type="button" onClick={open}>
          {I18n.t('generic.show_more')}
        </button>
      </div>
    )
  }

  function ReviewImages() {
    const { openGallery } = useLightgallery()

    const open = React.useCallback(
      index => {
        openGallery('travelerPhotos', index)
      },
      [reviewImages]
    )

    return (
      <div className="reviewImages">
        <Swiper
          navigation={{
            nextEl: '.button-next',
            prevEl: '.button-prev',
            disabledClass: 'button-disabled',
          }}
          slidesPerView={'auto'}
          slidesPerGroup={1}
          spaceBetween={4}
          className="mainSwiper"
          breakpoints={{
            600: {
              spaceBetween: 8,
            },
          }}
        >
          {reviewImages.slice(0, 4).map((reviewImage, index) => (
            <SwiperSlide key={reviewImage.id}>
              <div onClick={() => open(index)}>
                <img src={reviewImage.image_url} alt={reviewImage.description} loading="lazy" />
              </div>
            </SwiperSlide>
          ))}
          <button type="button" className="button-prev" aria-label="Prev Image">
            <SvgIcon
              name="keyboard_arrow_left"
              className="icon"
              ariaHidden={true}
              role="presentation"
            />
          </button>
          <button type="button" className="button-next" aria-label="Next Image">
            <SvgIcon
              name="keyboard_arrow_right"
              className="icon"
              ariaHidden={true}
              role="presentation"
            />
          </button>
        </Swiper>
      </div>
    )
  }

  React.useEffect(() => {
    syncReviewImages()
  }, [])

  return (
    <Wrapper>
      {reviewImages.length > 0 && (
        <div className="TravelerGallery_Main">
          <div>
            <h2>{I18n.t('post.traveler_photos')}</h2>
            <div className="TravelerGallery_Content">
              <LightgalleryProvider
                plugins={['lg-fullscreen.js', 'lg-zoom.js']}
                lightgallerySettings={{
                  zoom: false,
                  fullScreen: false,
                  download: false,
                }}
              >
                {reviewImages.map(reviewImage => (
                  <LightgalleryItem
                    key={reviewImage.id}
                    group="travelerPhotos"
                    src={reviewImage.image_url}
                  />
                ))}
                <ShowMoreReviewImages />
                <ReviewImages />
              </LightgalleryProvider>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .TravelerGallery_Main {
    padding-bottom: 4rem;
    margin-bottom: 4rem;
    border-bottom: 1px solid ${constants.COLORS.divider2};
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      padding-bottom: 2.4rem;
      margin-bottom: 2.4rem;
    }
  }

  .TravelerGallery_Content {
    position: relative;
    .ShowAll {
      position: absolute;
      top: -5.6rem;
      right: 0;
      margin: 0;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        top: -4.4rem;
      }
    }
  }

  .mainSwiper {
    .swiper-slide {
      img {
        @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
          border-radius: 0;
        }
      }
    }
  }
  .navSwiper {
    margin-top: 1.6rem;
    img {
      border: 1px solid ${constants.COLORS.divider};
    }
    .swiper-slide-thumb-active {
      img {
        border-color: ${constants.COLORS.primary};
      }
    }
  }
  .swiper-slide {
    width: auto;
    img {
      width: auto;
      height: 28rem;
      vertical-align: bottom;
      @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
        height: 25rem;
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        height: 20rem;
      }
    }
  }

  .button-prev,
  .button-next {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    width: 4rem;
    height: 4rem;
    background-color: rgba(0, 0, 0, 0.5);
    border: 0;
    border-radius: 50%;
    transform: translateY(-50%);
    transition: background-color 0.2s;
    z-index: 1000;
    cursor: pointer;
    .icon {
      color: ${constants.COLORS.white};
      width: 2.4rem;
      height: 2.4rem;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.75);
    }
    &.button-disabled {
      background-color: rgba(0, 0, 0, 0.25);
      .icon {
        color: ${constants.COLORS.disabled};
        opacity: 0.5;
        cursor: default;
      }
    }
  }
  .button-prev {
    left: 1.6rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      left: 1.2rem;
    }
  }
  .button-next {
    right: 1.6rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      right: 1.2rem;
    }
  }
`
